import { useEffect, useState, Fragment, useRef } from 'react';
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Header, Container, FormHeader } from "../../pages/common";

import axios from '../../axios';

import { Paper, Card, CardHeader, CardContent, Breadcrumbs, Grid, Button } from "@mui/material";

import SubmitButton from './SubmitButton';
import ClearButton from './ClearButton';

// snackbar
import useSnackbarStore from '../../store/snackbar';

// useQuery
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';

// loading component
import LoadingPageComponent from './LoadingPageComponent';

// error handler
import handleErrors  from '../errorHandler';

// Dialog hook
import useFormDialog from '../../hooks/useFormDialog';
import { useDialogForm } from '../../contexts/DialogForm';

import { useNewValue } from '../../contexts/NewValue';

// context
import { FormDataContext, useFormData } from '../../contexts/FormData';

import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';

import { Form, Formik } from 'formik';

import Field from '../FormikFields/Field';

import CustomAutocomplete from '../FormikFields/AutocompleteDebounced';

import { SectionBreak, FullDivider, InputLabel } from '../Common';

import AutoCompleteSoloFormik from '../FormikFields/AutoCompleteSolo';

import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';

import { convertToFormData } from '../util';

import useIsMobile from '../../hooks/useIsMobile';

import UploadProgress from './UploadProgress';

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  `;

const EditForm = ({ children,
  sidebar,
  title,
  initialValues = {}, 
  additionalState = {}, 
  schema = null, 
  fields, 
  submitFunc, 
  loading, 
  url, 
  validateOnChange = true, 
  dialog = false,
  loadOnDialog = true, 
  edit_id = null, 
  ignore_id = false, // ignore the id field in the url
  parseData = (data) => data, 
  additionalButtons, 
  submitButton = null, 
  url_params = {}, 
  collection_id = null,
  update_fields = null,
  redirect = null,
  redirect_to_edit = false,
  keep_open = false,
  invalidate_queries = [],
  show_progress = false,
  saveText = '',
  ...props 
}) => {

  const location = useLocation();
  const { pathname } = location;
  let isViewEdit = pathname.includes('/view');
  const baseUrl = pathname.split(isViewEdit ? '/view' : '/edit')[0];
  const { id } = useParams();
  let editId = edit_id ? edit_id : (ignore_id ? null : id);
  const isEdit = editId ? true : false;
  const modifier = isEdit ? 'Edit' : 'Add';

  const [formValues, setFormValues] = useState(additionalState);

  const [updateProgress, setUpdateProgress] = useState(0);

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const { handleClose } = useDialogForm() || {};

  const { addNewValue } = useNewValue() || {};

  const initialQueries = useRef({});

  const redirect_url = useRef(redirect || null);

  const redirect_to_edit_ref = useRef(false);

  const force_close_ref = useRef(false);

  let useDialog = handleClose ? true : false;

  useEffect(() => {
    redirect_url.current = redirect
  }, [redirect])


  useEffect(() => {
    redirect_to_edit_ref.current = redirect_to_edit
  }, [redirect_to_edit])

  const getQueries = (queries = {}) => {
    let queryStr = '';
    Object.keys(queries).forEach((key, i) => {
      queryStr = queryStr + (i === 0 ? `?${key}=${queries[key]}` : `&${key}=${queries[key]}`)
    })
    return queryStr;
  }

  const update = async ({ userData, formikHelpers, queries, timeout }) => {
    let config = { 
      timeout: timeout, 
      onUploadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        // Only update state if the percentage actually changed by at least 1%
        if (percent > updateProgress && show_progress) {
          setUpdateProgress(percent);
        }
      }
     };
    let queryStr = getQueries(queries);
    // console.log(queryStr, "queryStr");
    if (url_params?.post && !isEdit) {
      url = `${url}/${url_params.post}`
    }
    if (url_params?.put && isEdit && loadOnDialog) {
      url = `${url}/${url_params.put}`
    }
    try {
      let query = isEdit && loadOnDialog ? axios.put(`/${url}/${editId}${queryStr}`, userData, config) : axios.post(`/${url}${queryStr}`, userData, config);
      const { data } = await query;
      return data;
    } catch (e) {
      if (e?.code === 'ECONNABORTED') {
        console.error('The upload took too long and timed out.');
      }
      throw e;
    }
  }

  const getInfo = async () => {
    try {
      const { data } = await axios.get(`/${url}/${editId}`);
      data.backend_data_loaded = true;
      return data;
    } catch (e) {
      throw e;
    }
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: [url, editId],
    queryFn: getInfo,
    staleTime: 5 * 60 * 1000,
    enabled: isEdit && loadOnDialog
  })

  useEffect(() => {
    if (isEdit && loadOnDialog && editId) {
      refetch();
    }
  }, [editId]);

  // useEffect(() => {
  //   if (isEdit && loadOnDialog && editId) {
  //     refetch();
  //   }
  // }, [fields]);

  const updateMutation = useMutation({
    mutationFn: update,
    mutationKey: ["update", url],
    onSuccess: (data, { formikHelpers }) => {
      // console.log(data);
      showSnackbar('Saved successfully!', 'success');
      formikHelpers.setSubmitting(false);
      queryClient.invalidateQueries({ queryKey: [url] });

      console.log('invalidate_queries', invalidate_queries);
      if (invalidate_queries?.length) {
        invalidate_queries.forEach((query) => {
          console.log(query);
          queryClient.invalidateQueries({ queryKey: query });
        })
      }

      if (addNewValue) {
        addNewValue(data);
      }

      if (collection_id) {
        formikHelpers.setFieldValue(collection_id, data[collection_id]);
      }

      if (update_fields?.length) {
        update_fields.forEach((field) => {
          formikHelpers.setFieldValue(field, data[field]);
        })
      }

      formikHelpers.setFieldValue("finished_processing", true);

      if (redirect_url?.current) {
        return navigate(redirect_url?.current);
      }
      
      if (redirect_to_edit_ref?.current) {
        return navigate(`${baseUrl}/edit/${data[collection_id] || data?.id}`);
      }

      if (!useDialog && !dialog) {
        navigate(`/${url}`);
      }
    },
    onError: (error, { formikHelpers }) => {
      let message = handleErrors(error, formikHelpers.setErrors);
      if (message) showSnackbar(message, 'error');
      formikHelpers.setSubmitting(false);
    }
  });

  const handleSubmit = async (values, formikHelpers) => {
    let dataPacket = {...values};
    if (additionalState) {
      dataPacket = {...dataPacket, ...formValues};
    }
    // console.log(dataPacket, "datapacket***************");

    dataPacket = convertToFormData(dataPacket);

    // console.log(dataPacket.entries(), "datapacket***************");


    if (dataPacket instanceof FormData) {
      for (let [key, value] of dataPacket.entries()) {
        if (value instanceof File) {
          // console.log(key, `File: ${value.name} (${value.size} bytes)`);
        } else {
          console.log(key, value);
        }
      }
    }

    if (submitFunc) return submitFunc(dataPacket, formikHelpers);

    try {
      let mutationResult = await updateMutation.mutateAsync({ 
        userData: dataPacket, 
        formikHelpers,
        queries: initialQueries.current,
        timeout: dataPacket instanceof FormData  ? 300000 : 60000
      });
      if (keep_open && !force_close_ref?.current) {
        return;
      }
      if (useDialog) {
        handleClose();
      }
      // return { success: true, data: "done" };
    } catch (e) {
      console.error("Form submission error:", e);
      // return { success: false, error: e };
    }
  }

  const cardOptions = useDialog ? {elevation: 0} : {variant: 'outlined'};


  return (
  <Container>
    {!useDialog ? 
    <Breadcrumbs aria-label="breadcrumb" sx={{ marginBottom: '1rem' }}>
      <StyledLink to={baseUrl}>{title}s</StyledLink>
      <StyledLink to={pathname}>{modifier} {title}</StyledLink>
    </Breadcrumbs> : null}

    <Formik
      initialValues={data ? parseData(data) : initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
      enableReinitialize={true}
      validateOnChange={validateOnChange}
    >
    {(formikBag) => {
      return(
      <FormDataContext.Provider value={{formValues, setFormValues, refetch}}>
        <UploadProgress progress={updateProgress} isLoading={updateMutation?.isPending} use={show_progress} />
        <Grid container spacing={3}>
          <Grid size={{xs: 12, lg: sidebar ? 9 : 12}}>
            <Card {...cardOptions}>
              
              {!useDialog ? <CardHeader title={`${title} Details`} />: null}
              {loading || isLoading ? <LoadingPageComponent /> : <CardContent>
                <InnerForm childrenFields={children} fields={fields} {...formikBag} title={title} updateMutation={updateMutation} isEdit={isEdit} additionalButtons={additionalButtons} initialQueries={initialQueries} redirect_url={redirect_url} redirect_to_edit_ref={redirect_to_edit_ref} submitButton={submitButton} force_close_ref={force_close_ref} saveText={saveText} />
              </CardContent>}
            </Card>
          </Grid>

          { sidebar ? <Grid size={{xs: 12, lg: 3}}>
            {sidebar}
          </Grid> : null }

        </Grid>
      </FormDataContext.Provider>
    )}}
    </Formik>
  </Container>
  );
}

const getServerOptions = (params, vals) => {
  let loaded = true;
  if (params.formUpdates) {
    let result = {[params.fieldName]: vals[params.fieldName]?.toString()}
    params.formUpdates.forEach(el => {
      // console.log(vals[el.name]);
      // NOT SURE ABOUT THIS
      // if (!vals[el.name]) {
      //   loaded = false;
      // } else {
      //   result[el.field] = vals[el.name]
      // }
      if (vals[el.name]) {
        result[el.field] = vals[el.name]
      }
    });
    return params.serverOptions?.length ? params.serverOptions : loaded ? [result] : null;
  } else {
    let result = {[params.fieldName]: vals[params.name]?.toString()}
    if (!vals[params.name]) {
      loaded = false;
    }
    return params.serverOptions?.length ? params.serverOptions : loaded ? [result] : null;
  }
}

const InnerForm = ({childrenFields, fields, title, updateMutation, additionalButtons = [], submitButton, initialQueries, redirect_url, redirect_to_edit_ref, isEdit, force_close_ref, saveText, ...formikBag}) => {

  const [backendLoaded, setBackendLoaded] = useState(false);

  const { isMobile } = useIsMobile();

  const { errors, isSubmitting, validateForm, handleSubmit, submitForm, values } = formikBag;

  // console.log(errors, "values");  

  const { formValues, setFormValues } = useFormData() || {};

  const { handleClose } = useDialogForm() || {};

  let useDialog = handleClose ? true : false;

  let visibleButtons = additionalButtons.filter(button => !button.is_visible || button.is_visible(values, isEdit));

  const hideSubmitButton = typeof submitButton === 'function' 
  ? submitButton(values, isEdit) 
  : Boolean(submitButton);


  useEffect(() => {
    if (backendLoaded) {
      fields = fields.map(field => {
      if (field.fieldType === "debounceSearchSolo" || field.fieldType === "debounceSearch") {
        field.serverOptions = getServerOptions(field, values);
      }
      return field
      })
      formikBag.setFieldValue('backend_data_loaded', false);
    }
  }, [backendLoaded, fields])

  useEffect(() => {
    if (values?.backend_data_loaded) {
      setBackendLoaded(true);
    }
  }, [values?.backend_data_loaded]);

  return (
    <Form>
      <Grid container spacing={3} sx={{ marginTop: '2rem'}}>
        {fields ?  fields.map(({size, fieldType, formFieldType, fieldSize = "small", checkDisplayField, checkDisabled, ...fieldParams}, index) => {
          if (checkDisplayField && !checkDisplayField(values)) return null;
          let disabled = checkDisabled ? checkDisabled(values) : false;
          switch (fieldType) {
          case 'divider': {
            return <SectionBreak key={fieldParams.header} header={fieldParams.header} /> 
          }
          case 'debounceSearch': {
            return (
              <Grid size={size} key={fieldParams.name}>
                <InputLabel>{fieldParams.label}</InputLabel>
                <CustomAutocomplete {...fieldParams} disabled={disabled} size={fieldSize} /> 
              </Grid>
            );
          }
          case 'debounceSearchSolo': {
            return (
              <Grid size={size} key={fieldParams.name}>
                <InputLabel>{fieldParams.label}</InputLabel>
                <AutoCompleteSoloFormik {...fieldParams} disabled={disabled} size={fieldSize}  /> 
              </Grid>
            );
          }
          case 'Component': {
            return (
              // <Grid size={size} key={fieldParams.name}>
              <Fragment key={fieldParams.name}>
                {fieldParams.Component}
               </Fragment> 
             // </Grid>
            );
          }
          default:
            return (
              <Grid size={size} key={fieldParams.name}>
                <Field {...fieldParams} disabled={disabled} formFieldType={formFieldType} /> 
              </Grid>
            );
          } 
        }) : null}

        {childrenFields}

        <>
        <Grid size={12}>
          <FullDivider my="1rem" />
        </Grid>

        <Grid size={{xs: 0}}>
        </Grid>
        <Grid size={{xs: 12}} sx={{ display: 'flex', justifyContent: 'flex-end', gap: "1rem", flexDirection: isMobile ? "column" : "row" }}>
          {useDialog ? <Button onClick={handleClose} startIcon={<HighlightOffRoundedIcon />}>Cancel</Button> : null}
          <ClearButton>Clear</ClearButton>
           {visibleButtons?.length ? visibleButtons.map(({text: buttonText, queries, variant, color, redirect, redirect_to_edit, forceClose, icon}, i) => {
            let startIcon = icon ? {startIcon: icon} : {};
            return(
              <Button key={i} variant={variant} color={color} type='button' {...startIcon} onClick={(e) => {
                e.stopPropagation();
                if (redirect) {
                  redirect_url.current = redirect
                }
                if (redirect_to_edit) {
                  redirect_to_edit_ref.current = redirect_to_edit
                }
                if (forceClose) {
                  force_close_ref.current = forceClose
                }
                initialQueries.current = queries;
                handleSubmit(formikBag.values, formikBag);
              }} loading={isSubmitting || updateMutation.isPending}>{buttonText}</Button>
            )}) : null}
          {!hideSubmitButton ? <SubmitButton loading={isSubmitting || updateMutation.isPending} buttonType='button'
            onClick={() => handleSubmit(formikBag.values, formikBag)}
          >{saveText ? saveText : `${isEdit ? "Update" : "Save"} ${title}`}</SubmitButton> : null}
        </Grid>
        </> 
        {/* : null} */}
      </Grid>
    </Form>
  )
}

export default EditForm;